import { Fragment } from 'react';
import type { JobEndDetail, ActivityRating, ActivityType, SeniorFeedbackReviewer, SkillRatingScale, SkillArea, SkillDisplayMode, SimplifiedRating } from '@/api/types/jobEnd.types';
import { toSimplifiedRating } from '@/api/types/jobEnd.types';
import type { JobEndAuditRecord } from '@/api/types/audit.types';
import { RATING_LABELS } from '@/utils/ratingLabels';
import {
  ACTIVITY_LABELS,
  ACTIVITY_RATING_LABELS,
  ACTIVITY_RATING_OPTIONS,
} from './natureOfRoleLabels';
import { formatDate, formatDateTimeWithSeconds as formatDateTime } from '@/utils/dateFormatter';
import DOMPurify from 'dompurify';

// Sanitize HTML to prevent XSS attacks
const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'ol', 'li'] });
};

const SKILL_RATING_OPTIONS: SkillRatingScale[] = ['BT', 'OT_Minus', 'OT', 'OT_Plus', 'AT', 'O', 'NA'];
const OVERALL_RATING_OPTIONS: SkillRatingScale[] = ['BT', 'OT_Minus', 'OT', 'OT_Plus', 'AT', 'O'];

interface JobEndPdfContentProps {
  jobEnd: JobEndDetail;
  showNatureOfRole: boolean;
  showSkillRatings: boolean;
  skillRatingsDisplayMode: SkillDisplayMode;
  seniorFeedbacks: SeniorFeedbackReviewer[];
  showSeniorFeedback: boolean;
  showStrengthsDev: boolean;
  auditRecords: JobEndAuditRecord[];
  showAuditHistory: boolean;
}

const SIMPLIFIED_LABELS: Record<SimplifiedRating, string> = {
  ME: 'Meets Expectations',
  ND: 'Needs Development',
  NA: 'Not Applicable',
};

const styles = {
  container: {
    fontFamily: '"Nunito Sans", "Segoe UI", "Helvetica Neue", "Arial", sans-serif',
    fontSize: '12px',
    lineHeight: '1.5',
    color: '#333',
    padding: '40px',
    backgroundColor: '#fff',
    width: '714px',
  } as React.CSSProperties,
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: '24px',
    color: '#1a1a1a',
    borderBottom: '2px solid #00695c',
    paddingBottom: '8px',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 700,
    marginTop: '24px',
    marginBottom: '12px',
    color: '#00695c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px 24px',
    marginBottom: '8px',
  } as React.CSSProperties,
  detailRow: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  detailLabel: {
    fontWeight: 600,
    minWidth: '140px',
    color: '#555',
  } as React.CSSProperties,
  detailValue: {
    color: '#1a1a1a',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '8px',
    fontSize: '11px',
  } as React.CSSProperties,
  th: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'left' as const,
    fontWeight: 600,
    fontSize: '11px',
    color: '#555',
  } as React.CSSProperties,
  thCenter: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'center' as const,
    fontWeight: 600,
    fontSize: '11px',
    color: '#555',
  } as React.CSSProperties,
  td: {
    border: '1px solid #ddd',
    padding: '6px 8px',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,
  tdCenter: {
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  mainAreaBanner: {
    border: '1px solid #ddd',
    padding: '6px 8px',
    backgroundColor: '#00677D',
    color: '#ffffff',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  feedbackItem: {
    marginBottom: '12px',
    paddingLeft: '8px',
  } as React.CSSProperties,
  feedbackNumber: {
    fontWeight: 600,
    color: '#555',
    marginBottom: '4px',
  } as React.CSSProperties,
  feedbackContent: {
    lineHeight: '1.6',
  } as React.CSSProperties,
};


function getRatingSymbol(
  ratings: ActivityRating[],
  activityType: ActivityType,
  target: string,
): string {
  const rating = ratings.find((r) => r.activityType === activityType);
  return rating?.rating === target ? '\u25CF' : '\u25CB';
}

function getAreaRating(area: SkillArea): SkillRatingScale | null {
  if (area.skills.length === 0) return null;
  return area.skills[0].rating;
}

function startsMainArea(areas: SkillArea[], index: number): boolean {
  const mainArea = areas[index].mainArea;
  const previous = index > 0 ? areas[index - 1].mainArea : null;
  return !!mainArea && mainArea !== previous;
}

export function JobEndPdfContent({
  jobEnd,
  showNatureOfRole,
  showSkillRatings,
  skillRatingsDisplayMode,
  seniorFeedbacks,
  showSeniorFeedback,
  showStrengthsDev,
  auditRecords,
  showAuditHistory,
}: JobEndPdfContentProps) {
  const assignmentDate = jobEnd.assignmentEndDate
    ? `${formatDate(jobEnd.assignmentStartDate)} \u2014 ${formatDate(jobEnd.assignmentEndDate)}`
    : formatDate(jobEnd.assignmentStartDate);
  const ratings = jobEnd.activityRatings || [];
  const activities = jobEnd.natureOfRoleActivities || [];

  const hasContent = (html: string) => html.replace(/<[^>]*>/g, '').trim() !== '';
  const strengthsWithContent = (jobEnd.strengthFeedbacks || [])
    .filter((item) => hasContent(item.content))
    .sort((a, b) => a.orderIndex - b.orderIndex);
  const devAreasWithContent = (jobEnd.developmentAreaFeedbacks || [])
    .filter((item) => hasContent(item.content))
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const visibleSeniorFeedbacks = seniorFeedbacks.filter(
    (f) => !f.isDeletedByCollator,
  );

  return (
    <div style={styles.container}>
      <div data-pdf-section style={styles.title}>Job End</div>

      <div data-pdf-section>
        <div style={styles.sectionTitle}>Project Details</div>
        <div style={styles.detailsGrid}>
          <DetailRow label="Appraisee" value={`${jobEnd.appraiseeFirstName} ${jobEnd.appraiseeLastName}`} />
          <DetailRow label="Job End Author" value={`${jobEnd.collatorFirstName} ${jobEnd.collatorLastName}`} />
          <DetailRow label="Assignment Date" value={assignmentDate} />
          <DetailRow label="Tenure in Grade" value={jobEnd.tenureInGrade} />
          <DetailRow label="Client" value={jobEnd.clientName} />
          <DetailRow label="Project Name" value={jobEnd.projectName} />
          <DetailRow label="Project Code" value={jobEnd.projectCode} />
        </div>
      </div>

      {showNatureOfRole && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Nature of the Role</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Activity</th>
                {ACTIVITY_RATING_OPTIONS.map((option) => (
                  <th key={option} style={styles.thCenter}>{ACTIVITY_RATING_LABELS[option]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activities.map((activityType, index) => (
                <tr key={activityType}>
                  <td style={styles.td}>{index + 1}. {ACTIVITY_LABELS[activityType]}</td>
                  {ACTIVITY_RATING_OPTIONS.map((option) => (
                    <td key={option} style={styles.tdCenter}>{getRatingSymbol(ratings, activityType, option)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showSkillRatings && jobEnd.skillRatings && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Ratings by Skill Area</div>
          {skillRatingsDisplayMode === 'simplified' ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Skill Area</th>
                  <th style={{ ...styles.th, width: '30%' }}>Rating</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...styles.td, fontWeight: 600, backgroundColor: '#f5f5f5' }}>Overall Rating</td>
                  <td style={{ ...styles.td, backgroundColor: '#f5f5f5' }}>
                    {SIMPLIFIED_LABELS[toSimplifiedRating(jobEnd.skillRatings.overallRating)]}
                  </td>
                </tr>
                {jobEnd.skillRatings.skillAreas.map((area, index) => (
                  <Fragment key={area.areaId}>
                    {startsMainArea(jobEnd.skillRatings!.skillAreas, index) && (
                      <tr>
                        <td style={styles.mainAreaBanner} colSpan={2}>{area.mainArea}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={styles.td}>{index + 1}. {area.areaName}</td>
                      <td style={styles.td}>
                        {SIMPLIFIED_LABELS[toSimplifiedRating(getAreaRating(area))]}
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Skill Area</th>
                  {SKILL_RATING_OPTIONS.map((option) => (
                    <th key={option} style={styles.thCenter}>{RATING_LABELS[option]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...styles.td, fontWeight: 600, backgroundColor: '#f5f5f5' }}>Overall Rating</td>
                  {SKILL_RATING_OPTIONS.map((option) => (
                    <td key={option} style={{ ...styles.tdCenter, backgroundColor: '#f5f5f5' }}>
                      {OVERALL_RATING_OPTIONS.includes(option) && jobEnd.skillRatings!.overallRating === option ? '\u25CF' : OVERALL_RATING_OPTIONS.includes(option) ? '\u25CB' : ''}
                    </td>
                  ))}
                </tr>
                {jobEnd.skillRatings.skillAreas.map((area, index) => (
                  <Fragment key={area.areaId}>
                    {startsMainArea(jobEnd.skillRatings!.skillAreas, index) && (
                      <tr>
                        <td style={styles.mainAreaBanner} colSpan={SKILL_RATING_OPTIONS.length + 1}>
                          {area.mainArea}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={styles.td}>{index + 1}. {area.areaName}</td>
                      {SKILL_RATING_OPTIONS.map((option) => (
                        <td key={option} style={styles.tdCenter}>
                          {getAreaRating(area) === option ? '\u25CF' : '\u25CB'}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showStrengthsDev && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Feedback: Strengths</div>
          {strengthsWithContent.length > 0
            ? strengthsWithContent.map((item, index) => (
                <div key={item.id} style={styles.feedbackItem}>
                  <div style={styles.feedbackNumber}>{index + 1}.</div>
                  <div
                    style={styles.feedbackContent}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }}
                  />
                </div>
              ))
            : <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '8px' }}>No strengths recorded</div>
          }
        </div>
      )}

      {showStrengthsDev && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Feedback: Development Areas</div>
          {devAreasWithContent.length > 0
            ? devAreasWithContent.map((item, index) => (
                <div key={item.id} style={styles.feedbackItem}>
                  <div style={styles.feedbackNumber}>{index + 1}.</div>
                  <div
                    style={styles.feedbackContent}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.content) }}
                  />
                </div>
              ))
            : <div style={{ color: '#888', fontStyle: 'italic', marginBottom: '8px' }}>No development areas recorded</div>
          }
        </div>
      )}

      {showSeniorFeedback && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Senior Team Feedback</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '25%' }}>Reviewer</th>
                <th style={{ ...styles.th, width: '15%' }}>Status</th>
                <th style={styles.th}>Comments</th>
              </tr>
            </thead>
            <tbody>
              {visibleSeniorFeedbacks.map((feedback) => (
                <tr key={feedback.id}>
                  <td style={styles.td}>{feedback.reviewerName}</td>
                  <td style={styles.td}>{feedback.status}</td>
                  <td style={styles.td}>
                    {feedback.comments
                      ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(feedback.comments) }} />
                      : <span style={{ color: '#888', fontStyle: 'italic' }}>—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAuditHistory && auditRecords.length > 0 && (
        <div data-pdf-section>
          <div style={styles.sectionTitle}>Audit History</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '25%' }}>Date/Time</th>
                <th style={styles.th}>Activity</th>
                <th style={{ ...styles.th, width: '20%' }}>User</th>
              </tr>
            </thead>
            <tbody>
              {[...auditRecords]
                .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
                .map((record, index) => (
                  <tr key={`${record.occurredAt}-${index}`}>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formatDateTime(record.occurredAt)}</td>
                    <td style={styles.td}>{record.description}</td>
                    <td style={styles.td}>{record.performedBy}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}:</span>
      <span style={styles.detailValue}>{value}</span>
    </div>
  );
}
